import { useState, useCallback, useEffect, useMemo } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'

import type { MapHandle } from 'lib/maps/types'
import { focusCruisePortOnMap } from './cruiseItineraryMapUtils'
import { CruiseContentSections } from 'constants/cruise'
import useCruiseContentSectionLabels from 'hooks/Cruise/useCruiseContentSectionLabels'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { fetchCruiseOfferItinerary } from 'actions/CruiseActions'
import LayoutContainer from 'components/Common/LayoutContainer'
import Pane from 'components/Common/Pane'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import CruiseItinerarySidebar from './CruiseItinerarySidebar'
import CruiseItineraryItemOverview from './CruiseItineraryItemOverview'
import CruiseItineraryExperiences from './CruiseItineraryExperiences'
import CruiseItineraryPointsOfInterest from './CruiseItineraryPointsOfInterest'

const DEFAULT_ITINERARY: Array<App.CruiseItineraryItem> = []

const SectionContainer = styled.div`
  padding: ${rem(32)} 0;
`

const GridContainer = styled.div`
  display: block;

  ${mediaQueryUp.tablet} {
    display: grid;
    grid-template-columns: 1fr;
    gap: ${rem(24)};
    grid-template-columns: ${rem(280)} minmax(0, 1fr);
  }
`

interface Props {
  offerId: string
  itinerary?: Array<App.CruiseItineraryItem>
  cruiseType: App.CruiseType
  offerFallbackImageId?: string
}

function CruiseOfferPageItinerary({
  offerId,
  itinerary = DEFAULT_ITINERARY,
  cruiseType,
  offerFallbackImageId,
}: Props) {
  const dispatch = useAppDispatch()
  const sectionLabels = useCruiseContentSectionLabels()
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [portNameSelected, setPortNameSelected] = useState<string | undefined>(undefined)
  const [map, setMap] = useState<MapHandle | null>(null)

  const handleSelectItem = useCallback((index: number) => {
    setSelectedIndex(index)
  }, [])

  const onSelectMapPort = useCallback((portName: string | null) => {
    setPortNameSelected(portName === null ? undefined : portName)
  }, [])

  // Centralize map on port selected on sidebar (Mapbox handle)
  useEffect(() => {
    const item = itinerary[selectedIndex]
    if (!map) return
    if (item?.port) {
      setPortNameSelected(item.port.name)
      focusCruisePortOnMap(map, item.port)
    } else {
      setPortNameSelected(undefined)
    }
  }, [selectedIndex, itinerary, map])

  const offerItinerary = useAppSelector(state => state.cruise.cruiseOfferItinerary[offerId])

  useEffect(() => {
    dispatch(fetchCruiseOfferItinerary(offerId))
  }, [dispatch, offerId])

  const itineraryByDay = useMemo(() => {
    if (!offerItinerary?.itinerary) return undefined
    const map = new Map<number, App.Cruises.OfferItineraryV2Item>()
    for (const itineraryItem of offerItinerary.itinerary) {
      map.set(itineraryItem.startDay, itineraryItem)
    }
    return map
  }, [offerItinerary])

  const shipAmenities = offerItinerary?.shipAmenities
  const selectedItem = itinerary[selectedIndex]
  const selectedItineraryItem = itineraryByDay?.get(selectedItem?.startDay)
  const isAtSea = !selectedItem?.port

  if (!itinerary.length) {
    return null
  }

  return (
    <Pane>
      <LayoutContainer>
        <SectionContainer>
          <Group direction="vertical" gap={28}>
            <Heading
              align="center"
              variant="heading2"
              className="section-title"
            >
              {sectionLabels[CruiseContentSections.ITINERARY]}
            </Heading>

            <GridContainer>
              <CruiseItinerarySidebar
                itinerary={itinerary}
                cruiseType={cruiseType}
                selectedIndex={selectedIndex}
                onSelectItem={handleSelectItem}
                itineraryByDay={itineraryByDay}
                offerFallbackImageId={offerFallbackImageId}
              />

              {selectedItem && <Group direction="vertical" gap={24}>
                {selectedItineraryItem && (
                  <CruiseItineraryItemOverview
                    item={selectedItem}
                    itinerary={itinerary}
                    itineraryItem={selectedItineraryItem}
                    offerFallbackImageId={offerFallbackImageId}
                    portNameSelected={portNameSelected}
                    onSelectPort={onSelectMapPort}
                    onMapLoaded={setMap}
                  />
                )}
                <CruiseItineraryExperiences port={selectedItem.port} />
                <CruiseItineraryPointsOfInterest
                  portName={selectedItem.port?.name ?? selectedItem.title}
                  attractions={selectedItineraryItem?.attractions}
                  shipAmenities={shipAmenities}
                  isAtSea={isAtSea}
                />
              </Group>}
            </GridContainer>
          </Group>
        </SectionContainer>
      </LayoutContainer>
    </Pane>
  )
}

export default CruiseOfferPageItinerary
